import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Link, useSearchParams } from 'react-router-dom';
import { hubAPI } from '../services/hubApi';
import { programAPI } from '../services/programApi';
import { fetchCatalogResources } from '../services/resourceCatalogApi';
import { wordpressAPI } from '../services/wordpressApi';
import { WPPost } from '../types/wordpress';
import { Hub } from '../types/hub';
import { Program } from '../types/program';
import { CatalogResource } from '../types/resourceCatalog';
import ResourceCard from '../components/resources/ResourceCard';
import ToolkitListRow from '../components/hubs/ToolkitListRow';
import ProgramCard from '../components/programs/ProgramCard';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Pagination from '../components/layout/Pagination';
import { Squares2X2Icon, ListBulletIcon } from '@heroicons/react/24/outline';
import { filterHubs, hubHasDocuments, programMatchesQuery } from '../utils/hubSearch';
import { ORBCON_REPORT_CATEGORY_ID, ORBCON_REPORT_TITLE } from '../content/orbconReport';
import { ORDER_RESOURCES } from '../content/orderResources';
import { DATA_PROJECTS, dataProjectPath } from '../data/projects';
import { useAccessLevel } from '../hooks/useAccessLevel';
import { eventAPI } from '../services/eventApi';
import { AppEvent } from '../types/event';
import { placedDocumentIds } from '../utils/searchRank';
import { isUnderRevision } from '../utils/resourceRevision';

const RESOURCE_PAGE_SIZE = 20;

function catalogAsPost(resource: CatalogResource, wp?: WPPost): WPPost {
  const date = resource.wpCreatedAt || resource.wpModifiedAt || '';
  const underRevision = wp
    ? isUnderRevision(wp.acf?.under_revision)
    : resource.underRevision === true;
  return {
    id: resource.wpId,
    date,
    modified: wp?.modified || resource.wpModifiedAt || date,
    slug: resource.slug,
    parent: wp?.parent,
    link: resource.url,
    title: { rendered: resource.title },
    content: { rendered: '' },
    excerpt: { rendered: '' },
    featured_media: 0,
    categories: wp?.categories ?? [],
    tags: [],
    author: 0,
    acf: underRevision ? { under_revision: true } : undefined,
  };
}

export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || searchParams.get('search') || '';
  const programSlug = searchParams.get('program');
  const [posts, setPosts] = useState<WPPost[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [events, setEvents] = useState<AppEvent[]>([]);
  const { canViewData } = useAccessLevel();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [view, setView] = useState<'grid' | 'list'>('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);

  const matchingPrograms = useMemo(
    () =>
      (query.trim() ? programs.filter((program) => programMatchesQuery(program, query)) : []).filter(
        (program) => !programSlug || program.slug === programSlug
      ),
    [programs, query, programSlug]
  );
  const matchingHubs = useMemo(() => {
    if (!query.trim()) return [];
    const programIds = new Set(matchingPrograms.map((program) => program.id));
    return filterHubs(hubs, query).filter(
      (hub) =>
        hubHasDocuments(hub) &&
        !(hub.programId && programIds.has(hub.programId)) &&
        (!programSlug || hub.program?.slug === programSlug)
    );
  }, [hubs, query, matchingPrograms, programSlug]);

  const placedIds = useMemo(() => {
    if (!programSlug) return placedDocumentIds(hubs, programs);
    const program = programs.find((item) => item.slug === programSlug);
    if (!program) return new Set<number>();
    return placedDocumentIds(
      hubs.filter((hub) => hub.programId === program.id || hub.program?.slug === program.slug),
      [program]
    );
  }, [hubs, programs, programSlug]);
  const documentGroups = useMemo(() => {
    const placed: WPPost[] = [];
    const other: WPPost[] = [];
    const news: WPPost[] = [];
    for (const post of posts) {
      if (post.parent) continue;
      const onPlace = placedIds.has(post.id);
      if (programSlug && !onPlace) continue;
      if (post.categories.includes(ORBCON_REPORT_CATEGORY_ID)) news.push(post);
      else if (onPlace) placed.push(post);
      else other.push(post);
    }
    return { placed, other, news };
  }, [placedIds, posts]);
  const visibleDocuments =
    documentGroups.placed.length + documentGroups.other.length + documentGroups.news.length;
  const totalResults = matchingPrograms.length + matchingHubs.length + visibleDocuments;

  useEffect(() => {
    setCurrentPage(1);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    eventAPI
      .fetchEvents()
      .then((rows) => {
        if (!cancelled) setEvents(rows);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!query.trim()) {
        setPosts([]);
        setHubs([]);
        setPrograms([]);
        setTotalPosts(0);
        setTotalPages(1);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const catalogParams = new URLSearchParams({
          q: query,
          perPage: String(RESOURCE_PAGE_SIZE),
          page: String(currentPage),
          sort: 'relevance',
        });

        const [catalog, hubData, programData] = await Promise.all([
          fetchCatalogResources(catalogParams),
          hubAPI.fetchHubs().catch(() => [] as Hub[]),
          programAPI.fetchPrograms().catch(() => [] as Program[]),
        ]);
        const wpPosts = catalog.data.length
          ? await wordpressAPI
              .fetchPosts({
                include: catalog.data.map((resource) => resource.wpId).join(','),
                per_page: catalog.data.length,
              })
              .catch(() => [] as WPPost[])
          : [];
        const wpById = new Map(wpPosts.map((post) => [post.id, post]));

        setPosts(catalog.data.map((resource) => catalogAsPost(resource, wpById.get(resource.wpId))));
        setHubs(hubData);
        setPrograms(programData);
        setTotalPosts(catalog.meta.total);
        setTotalPages(Math.max(1, Math.ceil(catalog.meta.total / RESOURCE_PAGE_SIZE)));
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load search results'));
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [query, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasResults =
    matchingPrograms.length > 0 || matchingHubs.length > 0 || visibleDocuments > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Search Results</h1>
        {programSlug && (
          <p className="text-sm text-gray-600 mb-2">
            Searching{' '}
            {programs.find((program) => program.slug === programSlug)?.title ?? 'this place'}.{' '}
            <Link to={`/search?q=${encodeURIComponent(query)}`} className="font-medium text-primary">
              Search all of ORBCoN
            </Link>
          </p>
        )}
        {query && (
          <p className="text-gray-600">
            {loading ? (
              'Searching...'
            ) : (
              <>
                <span className="font-semibold text-gray-900">{totalResults}</span>{' '}
                {totalResults === 1 ? 'result' : 'results'} found for{' '}
                <span className="font-semibold text-gray-900">&quot;{query}&quot;</span>
              </>
            )}
          </p>
        )}
      </div>

      {!loading && hasResults && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <p className="text-sm text-gray-600">
            {matchingPrograms.length > 0 && (
              <>
                {matchingPrograms.length} {matchingPrograms.length === 1 ? 'place' : 'places'}
                {matchingHubs.length > 0 || totalPosts > 0 ? ' · ' : ''}
              </>
            )}
            {matchingHubs.length > 0 && (
              <>
                {matchingHubs.length} {matchingHubs.length === 1 ? 'toolkit' : 'toolkits'}
                {totalPosts > 0 ? ' · ' : ''}
              </>
            )}
            {visibleDocuments > 0 && (
              <>
                {visibleDocuments} {visibleDocuments === 1 ? 'document' : 'documents'}
              </>
            )}
          </p>
          {visibleDocuments > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setView('grid')}
                className={`p-2 rounded-md border transition-colors ${
                  view === 'grid'
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
                aria-label="Grid view"
              >
                <Squares2X2Icon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setView('list')}
                className={`p-2 rounded-md border transition-colors ${
                  view === 'list'
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
                aria-label="List view"
              >
                <ListBulletIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="space-y-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <LoadingSkeleton key={i} lines={4} className="h-64" />
          ))}
        </div>
      )}

      {error && (
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      )}

      {!loading && !error && !query && (
        <EmptyState
          title="Enter a search query"
          description="Use the search bar to find programs, toolkits, references, and resources."
        />
      )}

      {!loading && !error && query && !hasResults && (
        <EmptyState
          title="No results found"
          description={`We couldn't find any programs, toolkits, references, or resources matching "${query}". Try adjusting your search terms.`}
          actionLabel="Browse resources"
          onAction={() => {
            window.location.href = '/resources';
          }}
        />
      )}

      {!loading && !error && hasResults && (
        <>
          {matchingPrograms.some((program) => program.kind !== 'REFERENCE') && (
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Programs</h2>
              <div className="space-y-4">
                {matchingPrograms.filter((program) => program.kind !== 'REFERENCE').map((program) => (
                  <ProgramCard key={program.id} program={program} />
                ))}
              </div>
            </section>
          )}

          {matchingHubs.length > 0 && (
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Toolkits</h2>
              <div className="space-y-6">
                {matchingHubs.map((hub) => (
                  <ToolkitListRow key={hub.id} hub={hub} />
                ))}
              </div>
            </section>
          )}

          {matchingPrograms.some((program) => program.kind === 'REFERENCE') && (
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">References</h2>
              <div className="space-y-4">
                {matchingPrograms.filter((program) => program.kind === 'REFERENCE').map((program) => (
                  <ProgramCard key={program.id} program={program} />
                ))}
              </div>
            </section>
          )}

          {documentGroups.placed.length > 0 && (
            <DocumentGroup title="Resources" posts={documentGroups.placed} view={view} />
          )}
          {documentGroups.other.length > 0 && (
            <DocumentGroup title="Also matching the name" posts={documentGroups.other} view={view} />
          )}
          {documentGroups.news.length > 0 && (
            <DocumentGroup title={ORBCON_REPORT_TITLE} posts={documentGroups.news} view={view} />
          )}
          {!programSlug && (
            <OtherMatches query={query} events={events} canViewData={canViewData} />
          )}
          {totalPages > 1 && posts.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
}

function titleHit(query: string, title: string): boolean {
  const needle = query.trim().toLowerCase();
  return needle.length >= 2 && title.toLowerCase().includes(needle);
}

function OtherMatches({
  query,
  events,
  canViewData,
}: {
  query: string;
  events: AppEvent[];
  canViewData: boolean;
}) {
  const [glossaryText, setGlossaryText] = useState('');
  const [courses, setCourses] = useState<Array<{ slug: string; title: string }>>([]);
  const [pages, setPages] = useState<Array<{ slug: string; title: string }>>([]);
  const { getToken, isSignedIn } = useAuth();
  useEffect(() => {
    let cancelled = false;
    wordpressAPI
      .fetchPostBySlug('glossary-of-terms-definition-and-acronyms')
      .then((post) => {
        if (!cancelled) setGlossaryText(post?.content.rendered ?? '');
      })
      .catch(() => {
        if (!cancelled) setGlossaryText('');
      });
    if (!isSignedIn) {
      setCourses([]);
      return () => {
        cancelled = true;
      };
    }
    getToken()
      .then((token) =>
        fetch('/api/courses', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
      )
      .then((response) => (response.ok ? response.json() : []))
      .then((body) => {
        if (cancelled) return;
        const rows = Array.isArray(body) ? body : body?.courses ?? body?.data ?? [];
        setCourses(
          rows
            .filter((course: { slug?: string; title?: string }) => course.slug && course.title)
            .map((course: { slug: string; title: string }) => ({ slug: course.slug, title: course.title }))
        );
      })
      .catch(() => {
        if (!cancelled) setCourses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, isSignedIn]);
  useEffect(() => {
    const needle = query.trim();
    if (needle.length < 2) {
      setPages([]);
      return;
    }
    let cancelled = false;
    wordpressAPI
      .fetchPages({ search: needle, per_page: 5 })
      .then((rows) => {
        if (cancelled) return;
        setPages(
          rows
            .filter((page) => page.slug && page.title?.rendered)
            .map((page) => ({ slug: page.slug, title: page.title.rendered }))
        );
      })
      .catch(() => {
        if (!cancelled) setPages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [query]);
  const eventHits = events.filter((event) => titleHit(query, event.title)).slice(0, 3);
  const courseHits = courses.filter((course) => titleHit(query, course.title)).slice(0, 3);
  const orderHits = ORDER_RESOURCES.filter((resource) => titleHit(query, resource.title));
  const dataHits = canViewData ? DATA_PROJECTS.filter((project) => titleHit(query, project.title)) : [];
  const needle = query.trim().toLowerCase();
  const glossary =
    needle.length >= 2 &&
    (glossaryText.toLowerCase().includes(needle) || titleHit(query, 'glossary'));
  const pageHits = pages.filter((page) => titleHit(query, page.title)).slice(0, 3);
  if (
    eventHits.length === 0 &&
    courseHits.length === 0 &&
    orderHits.length === 0 &&
    dataHits.length === 0 &&
    pageHits.length === 0 &&
    !glossary
  ) {
    return null;
  }
  return (
    <section className="mb-10 space-y-6">
      {eventHits.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Events</h2>
          <ul className="space-y-2">
            {eventHits.map((event) => (
              <li key={event.id}>
                <Link to={`/events/${event.slug}`} className="text-primary font-medium">
                  {event.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {courseHits.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Courses</h2>
          <ul className="space-y-2">
            {courseHits.map((course) => (
              <li key={course.slug}>
                <Link to={`/elearning/courses/${course.slug}`} className="text-primary font-medium">
                  {course.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {orderHits.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Printed handbooks</h2>
          <Link to="/order-resources" className="text-primary font-medium">
            Order printed material
          </Link>
        </div>
      )}
      {dataHits.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Data</h2>
          <ul className="space-y-2">
            {dataHits.map((project) => (
              <li key={project.slug}>
                <Link to={dataProjectPath(project.slug)} className="text-primary font-medium">
                  {project.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {pageHits.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Pages</h2>
          <ul className="space-y-2">
            {pageHits.map((page) => (
              <li key={page.slug}>
                <Link to={`/page/${page.slug}`} className="text-primary font-medium">
                  {page.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {glossary && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Glossary</h2>
          <Link to="/references/glossary" className="text-primary font-medium">
            Glossary of terms
          </Link>
        </div>
      )}
    </section>
  );
}

function DocumentGroup({
  title,
  posts,
  view,
}: {
  title: string;
  posts: WPPost[];
  view: 'grid' | 'list';
}) {
  return (
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">{title}</h2>
      <div
        className={
          view === 'grid'
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
            : 'space-y-4'
        }
      >
        {posts.map((post) => (
          <ResourceCard key={post.id} post={post} view={view} />
        ))}
      </div>
    </section>
  );
}
